import { useEffect, useMemo, useRef, useState } from 'react';
import notify from 'devextreme/ui/notify';
import { alert } from 'devextreme/ui/dialog';

import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import {
    resolveServiceQuotaNotice,
    secondsUntilStaffNotice,
    SERVICE_QUOTA_ERROR_EVENT,
    OPEN_BILLING_TEXT,
    QUOTA_ERROR_TOAST_TEXT,
    POS_RESOLVE_GUIDE_TEXT
} from './serviceQuotaNotice';

const SOCKET_LISTENER_KEY = 'AntServiceQuotaNotice';
const ERROR_TOAST_COOLDOWN_MS = 10000;

let lastErrorToastAt = 0;

function nowUnix() {
    return Math.floor(Date.now() / 1000);
}

function escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
}

/** Hộp hướng dẫn xử lý trên POS, nơi không có màn gói dịch vụ. */
export function showServiceQuotaResolveGuide() {
    alert(escapeHTML(AntLocalization.translateText(POS_RESOLVE_GUIDE_TEXT)), AntLocalization.translateText(OPEN_BILLING_TEXT));
}

function showQuotaErrorToast(onOpen) {
    const isMobile = window.innerWidth <= 600;
    notify(
        {
            type: 'warning',
            width: Math.min(400, window.innerWidth - 24),
            displayTime: 5000,
            closeOnClick: false,
            elementAttr: { class: 'ant-service-quota-notification' },
            position: {
                my: isMobile ? 'top center' : 'top right',
                at: isMobile ? 'top center' : 'top right',
                of: window,
                offset: isMobile ? '0 64' : '-16 64'
            },
            contentTemplate: (element) => {
                const root = element && element.nodeType ? element : element && element[0];
                if (!root) return;
                const wrap = document.createElement('div');
                wrap.className = 'ant-service-quota-toast';
                const text = document.createElement('span');
                text.textContent = AntLocalization.translateText(QUOTA_ERROR_TOAST_TEXT);
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'ant-service-quota-toast-button';
                button.textContent = AntLocalization.translateText(OPEN_BILLING_TEXT);
                button.onclick = () => onOpen();
                wrap.appendChild(text);
                wrap.appendChild(button);
                root.appendChild(wrap);
            }
        }
    );
}

/**
 * Panel vượt hạn mức cho footer web: trả null hoặc { type, text, onClick }.
 * - Trạng thái lấy từ phiên (làm mới mỗi phút) và lấy lại ngay khi có sự kiện `service_quota_changed`.
 * - Nhân viên: tự hẹn giờ hiện đúng `staff_visible_at`.
 * - Admin: lỗi 133/135 từ API hiện toast kèm nút xem gói dịch vụ.
 *
 * @param {object} session AntSession()
 * @param {{ onOpenBilling: Function }} options mở màn gói dịch vụ (web) hoặc hộp hướng dẫn (POS)
 */
export default function useServiceQuotaNotice(session, options) {
    const [quota, setQuota] = useState(session ? session.service_quota || null : null);
    const [tick, setTick] = useState(0);
    const openBillingRef = useRef(null);
    openBillingRef.current = options && options.onOpenBilling;

    useEffect(() => {
        setQuota(session ? session.service_quota || null : null);
    }, [session]);

    useEffect(() => {
        if (!session || typeof session.registerSocketListener !== 'function') return undefined;
        let alive = true;
        const api = new AntAPI('sys', 'Business');
        session.registerSocketListener((data) => {
            if (!data || data.event !== 'service_quota_changed') return false;
            api.postActionWithData('actionGetServiceQuotaState', {}).then((res) => {
                if (!alive || !res || res.error || !res.data) return;
                setQuota(res.data.service_quota || null);
            });
            return true;
        }, SOCKET_LISTENER_KEY);
        return () => {
            alive = false;
            if (typeof session.clearSocketListener === 'function') session.clearSocketListener(SOCKET_LISTENER_KEY);
        };
    }, [session]);

    useEffect(() => {
        const seconds = secondsUntilStaffNotice(quota, nowUnix());
        if (!seconds) return undefined;
        // setTimeout tối đa ~24,8 ngày; staff_visible_at chỉ cách vài giờ nên chặn trên 1 ngày là đủ.
        const timer = setTimeout(() => setTick((value) => value + 1), Math.min(seconds, 86400) * 1000 + 500);
        return () => clearTimeout(timer);
    }, [quota, tick]);

    const isAdmin = Boolean(session) && session.user_group_code === 'Admin';
    useEffect(() => {
        if (!isAdmin || typeof window === 'undefined') return undefined;
        const onQuotaError = () => {
            if (!openBillingRef.current || Date.now() - lastErrorToastAt < ERROR_TOAST_COOLDOWN_MS) return;
            lastErrorToastAt = Date.now();
            showQuotaErrorToast(() => openBillingRef.current && openBillingRef.current());
        };
        window.addEventListener(SERVICE_QUOTA_ERROR_EVENT, onQuotaError);
        return () => window.removeEventListener(SERVICE_QUOTA_ERROR_EVENT, onQuotaError);
    }, [isAdmin]);

    return useMemo(() => {
        const notice = resolveServiceQuotaNotice(session, quota, nowUnix());
        if (!notice) return null;
        return {
            type: notice.type,
            text: AntLocalization.translateText(notice.message),
            onClick: notice.actionable ? () => openBillingRef.current && openBillingRef.current() : undefined
        };
    }, [session, quota, tick]);
}
